<template>
  <div class="messages-wrapper">
    <div class="content-wrapper">
      <CaseMessageDetailsNavigation />
      <CustomSearch placeholder="Find..." @update:modelValue="keySearch = $event" />
      <div class="scrollable-container">
        <SetMessages
          v-if="messages.length > 0"
          :messages="messages"
          :message-search="keySearch"
          edit-mode
          @changeCheckbox="changeMessageCheckbox"
        />
        <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler">
          <div slot="spinner">
            Loading...
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
        <SelectedMessagesInfo
          :messages="messages"
          :selected-messages-id="selectedMessagesId"
          :timer-started="timeRemaining"
          @updatedActiveStatus="updatedActiveStatus"
          @exportMessages="openExportPopup"
        />
      </div>
    </div>
    <MessageDetails />
    <CommonDialog
      ref="progressDialog"
      :show-footer="false"
      :closable="false"
    >
      <h2 class="dialog-message-title">
        Exporting messages
      </h2>
      <p class="dialog-create-description" style="display:block; font-size: 14px;">
        Please wait a bit. The messages are
        being exported.
      </p>
      <div class="loader-p" />
    </CommonDialog>
    <SuccessPopup
      ref="successPopup"
      text="A URL will be sent to your email with a download link. Do not share this link."
    />
    <CommonDialog ref="exportV2SuccessPopup" :show-footer="false">
      <SuccessIcon />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Chosen messages were exported, you can find them on case export page. Would you like to see exported files?
      </p>
      <div class="dialog-btn-wrapper">
        <button
          id="dialoge_proceed"
          class="btn btn-export"
          @click="goToExportPage"
        >
          <span class="dialog-btn-text">Continue</span>
        </button>
        <button
          id="dialoge_close"
          class="btn btn-close btn-revert"
          @click="closeSuccessExportV2Popup"
        >
          <span class="dialog-btn-text">Close</span>
        </button>
      </div>
    </CommonDialog>
    <ExportPopup
      ref="exportPopup"
      :is-filters-applied="false"
      :is-messages-export="true"
      @export="exportMessages"
    />
  </div>
</template>

<script>
import MessagesTaggingNavigationPanel
  from '@/components/cases/case-messages/message-tags/MessagesTaggingNavigationPanel.vue';
import SetMessages from '@/components/cases/case-messages/SetMessages.vue';
import CustomSearch from '@/components/inputs/CustomSearch.vue';
import MessageTaggingPanel from '@/components/cases/case-messages/message-tags/MessageTaggingPanel.vue';
import InfiniteLoading from 'vue-infinite-loading';
import SelectedMessagesInfo from '@/components/selected-messages-info/SelectedMessagesInfo.vue';
import SuccessPopup from '@/components/popups/SuccessPopup.vue';
import CommonDialog from '@/components/popups/CommonDialog.vue';
import streamReader from '@/mixins/streamReader';
import messagesTagging from '@/mixins/messagesTagging';
import exportTimer from '@/mixins/exportTimer';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import ExportPopup from '@/components/popups/ExportPopup.vue';

export default {
  name: 'MessagesTaggingHighlightTab',
  components: {
    SuccessIcon,
    ExportPopup,
    CommonDialog,
    SuccessPopup,
    SelectedMessagesInfo,
    MessageDetails: MessageTaggingPanel,
    CustomSearch,
    SetMessages,
    CaseMessageDetailsNavigation: MessagesTaggingNavigationPanel,
    InfiniteLoading,
  },
  mixins: [streamReader, messagesTagging, exportTimer],
  data() {
    return {
      infiniteId: +new Date(),
      selectedMessagesId: [],
      selectedMessagesIdCopy: [],
      isCheckedAllMessages: false,
      format: '',
      caseId: this.$route.params.slug,
    };
  },
  watch: {
    keySearch() {
      this.chatPage = 1;
      this.messages = [];
      this.infiniteId += 1;
    },
    messages: {
      deep: true,
      handler() {
        const validIds = this.messages.map(m => m.id);
        this.selectedMessagesId = this.selectedMessagesId.filter(id => validIds.includes(id));
        if (this.isCheckedAllMessages) {
          this.messages.map(message => message.isActive = true);
        } else {
          this.messages.map(message => {
            const messageId = this.selectedMessagesId.find(id => id === message.id);
            message.isActive = !!messageId;
          });
        }
      }
    },
  },
  mounted() {
    this.checkTimer('messagesExportEndTime');
  },
  methods: {
    async infiniteHandler($state) {
      await this.getListHighlightMessages();
      if (this.hasMoreMessages) {
        $state.loaded();
      } else {
        $state.complete();
      }
    },
    changeMessageCheckbox(item) {
      if (item.isActive && !this.selectedMessagesId.includes(item.id)) {
        this.selectedMessagesId.push(item.id);
      } else {
        this.selectedMessagesId = this.selectedMessagesId.filter(id => id !== item.id);
        item.isActive = false;
      }
    },
    updatedActiveStatus($event) {
      this.isCheckedAllMessages = $event.isCheckedAllMessages;
      this.selectedMessagesId = $event.selectedMessagesId;
      this.messages = $event.messages;
    },
    openExportPopup(format, data) {
      if (format) {
        this.format = format;
      }
      this.selectedMessagesIdCopy = data;
      this.$refs.exportPopup.openPopups();
    },
    exportMessages(format, config, applyTimeframes = false) {
      if (format === 'rsmf' || format === 'csv') {
        this.exportV1(format, applyTimeframes);
      } else if (format === 'pdf' || format === 'xml') {
        this.exportV2(format, config, applyTimeframes);
      }
      this.$refs.exportPopup.closePopups();
    },
    exportV1(format) {
      this.$load(async () => {
        this.$refs.progressDialog.openPopups();
        const response = await this.$api.cases.exportMessagesV1(
          this.caseId,
          format,
          {messageIds: this.selectedMessagesIdCopy}
        );
        // eslint-disable-next-line no-undef
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        await this.processStreamData(reader, 33);
        this.handleStreamResult();
        this.resetState();
        this.startExportTimer('messagesExportEndTime');
      }, (error) => {
        this.handleResponseError(error);
        if (error.response) {
          this.$emit('show-error', error.response.data.errors.message[0]);
        } else {
          this.$emit('show-error', error.data.errors.message[0]);
        }
      });
    },
    exportV2(format, config, applyTimeframes) {
      this.$load(async () => {
        this.$refs.progressDialog.openPopups();
        const response = await this.$api.cases.exportMessagesV2(
          this.caseId,
          format,
          applyTimeframes,
          {
            config: config,
            messages: {messageIds: this.selectedMessagesIdCopy},
          });
        // eslint-disable-next-line no-undef
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        await this.processStreamData(reader, 33);
        this.handleStreamResult(false);
        this.resetState();
        this.startExportTimer('messagesExportEndTime');
        this.$refs.exportV2SuccessPopup.openPopups();
      }, (error) => {
        this.handleResponseError(error);
        if (error.response) {
          this.$emit('show-error', error.response.data.errors.message[0]);
        } else {
          this.$emit('show-error', error.data.errors.message[0]);
        }
      });
    },
    goToExportPage() {
      this.$router.push({path: `/cases/${this.caseId}/export`});
    },
    closeSuccessExportV2Popup() {
      this.$refs.exportV2SuccessPopup.closePopups();
    },
    resetState() {
      this.selectedMessagesId = [];
      this.selectedMessagesIdCopy = [];
      this.isCheckedAllMessages = false;

      this.messages = this.messages.map(message => ({
        ...message,
        isActive: false,
      }));
    },
  },
};
</script>

<style scoped lang="scss">
.messages-wrapper {
  display: flex;
  margin: 25px 60px 0 40px;
  max-width: 90vw;

  .content-wrapper {
    position: relative;
    padding: 50px 0 0;
    min-width: 600px;
    width: 100%;

    .scrollable-container {
      max-height: 80.6vh;
      overflow-y: auto;
      scrollbar-color: var(--background-color) transparent;
      scrollbar-width: thin;
    }
  }

  .loader-p {
    width: 50px;
    height: 50px;
    position: relative;
    margin: 20px 40px 20px 0;
  }
}

.dialog-btn-wrapper {
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  gap: 20px;
}

.dialog-btn-wrapper .btn {
  height: 48px;
}

.dialog-btn-wrapper .btn-export {
  background-color: var(--accent-color);
  border: 1px solid var(--accent-color);
}

.dialog-btn-wrapper .btn-close:hover {
  background-color: var(--white-color);
}

.dialog-btn-wrapper .btn-export .dialog-btn-text {
  color: var(--white-color);
}

.dialog-btn-wrapper .btn-export:hover {
  color: var(--white-color);
}

.dialog-btn-wrapper .btn-export:hover {
  background-color: var(--accent-color);
  color: var(--white-color);
  border: 1px solid var(--accent-color);
}
</style>
